"use client";

import { useTranslation } from "react-i18next";
import CopyRow from "@/components/common/CopyRow";
import { Button } from "@/components/ui/button";
import type { JotFormWebhookTargetsStatus } from "@/hooks/useJotFormCredential";
import type { JotFormWebhookTarget } from "@/types/jotform";
import JotFormCallout from "./JotFormCallout";
import JotFormSection from "./JotFormSection";

interface JotFormWebhookUrlsSectionProps {
  /** Distinguishes "nothing was fetched" and "the read failed" from "none enabled". */
  state: JotFormWebhookTargetsStatus;
  targets: JotFormWebhookTarget[];
  truncated: boolean;
  onRetry: () => void;
}

export default function JotFormWebhookUrlsSection({
  state,
  targets,
  truncated,
  onRetry,
}: Readonly<JotFormWebhookUrlsSectionProps>) {
  const { t } = useTranslation("partners");
  const { t: ct } = useTranslation("common");

  return (
    <JotFormSection
      heading={t("jotformIntegration.webhookUrlsHeading")}
      testId="jotform-webhook-urls-section"
    >
      <p className="text-xs text-500">{t("jotformIntegration.webhookUrlsDescription")}</p>

      {state === "requiresSecret" && (
        <p
          className="text-xs text-500 italic"
          data-testid="jotform-webhook-urls-requires-secret"
        >
          {t("jotformIntegration.webhookUrlsRequiresSecret")}
        </p>
      )}

      {state === "failed" && (
        <div className="space-y-2" data-testid="jotform-webhook-urls-load-error">
          <p className="text-xs text-500">
            {t("jotformIntegration.webhookUrlsLoadError")}
          </p>
          <Button type="button" size="sm" variant="outline" onClick={onRetry}>
            {ct("retry")}
          </Button>
        </div>
      )}

      {state === "loaded" &&
        (targets.length === 0 ? (
          <p className="text-xs text-500 italic" data-testid="jotform-webhook-urls-empty">
            {t("jotformIntegration.webhookUrlsEmpty")}
          </p>
        ) : (
          targets.map((target) => {
            const row = (
              <CopyRow
                key={target.workflowId}
                label={target.workflowName}
                value={target.url}
                copiedMessage={t("jotformIntegration.urlCopied")}
                failedMessage={t("jotformIntegration.copyFailed")}
                copyLabel={t("jotformIntegration.copyUrl", {
                  workflow: target.workflowName,
                })}
                testId={`jotform-webhook-url-${target.workflowId}`}
              />
            );

            // Wrapped per row, not aggregated, so it is unambiguous which
            // workflow is dead. The URL stays copyable — it becomes correct
            // again the moment the webhook goes back on.
            return target.live ? (
              row
            ) : (
              <JotFormCallout
                key={target.workflowId}
                tone="amber"
                message={t("jotformIntegration.webhookUrlsNotLive")}
                testId={`jotform-webhook-url-not-live-${target.workflowId}`}
              >
                {row}
              </JotFormCallout>
            );
          })
        ))}

      {truncated && (
        <JotFormCallout
          tone="amber"
          message={t("jotformIntegration.webhookUrlsTruncated")}
          testId="jotform-webhook-urls-truncated"
        />
      )}
    </JotFormSection>
  );
}
